import * as React from "react";
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { AppContext } from "../../AppContextProvider";
import {
  rcChat,
  refreshSessionRequestForQuotes,
} from "../../api/ProcurementClient";
import {
  mergeFetchedRfqIntoList,
  snapshotRfqClientEpochs,
} from "../../utils/mergeRfqIntoList";
import {
  executeClientActions,
  RcClientAction,
} from "./rcClientActions";
import {
  nextMessageId,
  RcChatMessage,
  RcChatResponse,
  RcPageContext,
  RcPendingConfirmation,
} from "./rcTypes";
import { speakText, useSpeechRecognition } from "./useSpeechRecognition";
import { RcContext, RcContextValue } from "./RcContext";
import RcFab from "./RcFab";
import RcPanel from "./RcPanel";
import { getLastWorkQueueRfqId } from "../../utils/purchaseOrderWorkQueue";

export { useRc } from "./RcContext";

function parsePageContext(
  pathname: string,
  role?: string,
  focusedHomeworkItemId?: number
): RcPageContext {
  const ctx: RcPageContext = { pathname, role };
  const rfqMatch = pathname.match(/^\/rfq\/(\d+)/);
  if (rfqMatch) ctx.rfqId = Number(rfqMatch[1]);
  const emailMatch = pathname.match(/^\/emails\/(\d+)/);
  if (emailMatch) ctx.emailId = Number(emailMatch[1]);
  const poMatch = pathname.match(/^\/purchase-orders\/(\d+)/);
  if (poMatch) ctx.poId = Number(poMatch[1]);
  const pqMatch = pathname.match(/^\/price-quotes\/(\d+)/);
  if (pqMatch) ctx.priceQuoteId = Number(pqMatch[1]);
  if (focusedHomeworkItemId && focusedHomeworkItemId > 0) {
    ctx.focusedHomeworkItemId = focusedHomeworkItemId;
  }
  const lastRfqId = getLastWorkQueueRfqId();
  if (lastRfqId) ctx.lastRfqId = lastRfqId;
  return ctx;
}

export const RcProvider: React.FC<{ children?: React.ReactNode }> = ({
  children,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, setSnackbarErrorMsg, setRfqs } = useContext(AppContext);

  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<RcChatMessage[]>([]);
  const [thinking, setThinking] = useState(false);
  const [conversationId, setConversationId] = useState<string | undefined>();
  const [pendingConfirmations, setPendingConfirmations] = useState<
    RcPendingConfirmation[]
  >([]);
  const [focusedHomeworkItemId, setFocusedHomeworkItemId] = useState<
    number | undefined
  >();
  const focusedHomeworkItemIdRef = useRef<number | undefined>();
  focusedHomeworkItemIdRef.current = focusedHomeworkItemId;
  const conversationIdRef = useRef<string | undefined>();
  conversationIdRef.current = conversationId;
  const thinkingRef = useRef(false);
  const sendQueueRef = useRef<string[]>([]);

  const sendTextRef = useRef<(text: string) => Promise<void>>(async () => {});

  const handleFinalTranscript = useCallback((text: string) => {
    void sendTextRef.current(text);
  }, []);

  const {
    listening,
    interim,
    error: speechError,
    setError: setSpeechError,
    start,
    stop,
    setTtsMuted,
    supported: speechSupported,
  } = useSpeechRecognition({ onFinalTranscript: handleFinalTranscript });

  const applyResponse = useCallback(
    async (
      res: RcChatResponse | import("../../api/ProcurementClient").RcChatApiResponse
    ) => {
      const normalized: RcChatResponse = {
        reply: res.reply,
        conversationId: res.conversationId,
        clientActions: (res.clientActions || []) as RcClientAction[],
        pendingConfirmations: res.pendingConfirmations || [],
        invalidate: res.invalidate,
      };
      setConversationId(normalized.conversationId);
      setPendingConfirmations(normalized.pendingConfirmations ?? []);
      setMessages((prev) => [
        ...prev,
        {
          id: nextMessageId(),
          role: "assistant",
          content: normalized.reply,
          pendingConfirmations: normalized.pendingConfirmations,
        },
      ]);
      if (normalized.clientActions?.length) {
        for (const action of normalized.clientActions) {
          if (action.type === "open_line_item") {
            setFocusedHomeworkItemId(action.homeworkItemId);
          }
        }
        executeClientActions(normalized.clientActions, navigate);
      }
      if (normalized.invalidate?.includes("rfqs")) {
        try {
          const epochsAtStart = snapshotRfqClientEpochs();
          const rfqs = await refreshSessionRequestForQuotes();
          setRfqs((prev) => {
            const prevById = new Map(prev.map((r) => [r.id, r]));
            return rfqs.map((rfq) => {
              const local = prevById.get(rfq.id);
              if (!local) return rfq;
              return mergeFetchedRfqIntoList(
                [local],
                rfq,
                epochsAtStart.get(rfq.id) ?? 0
              )[0];
            });
          });
        } catch {
          /* optional refresh */
        }
      }
      if (normalized.reply) {
        // Mute mic while speaking so RC doesn't hear itself / double-trigger.
        setTtsMuted(true);
        try {
          await speakText(normalized.reply);
        } finally {
          setTtsMuted(false);
        }
      }
    },
    [navigate, setRfqs, setTtsMuted]
  );

  const sendText = useCallback(
    async (text: string) => {
      const trimmed = text.trim();
      if (!trimmed) return;

      // Queue while a turn is in flight so continuous listening doesn't drop speech.
      if (thinkingRef.current || sendQueueRef.current.length > 0) {
        sendQueueRef.current.push(trimmed);
        return;
      }

      const run = async (message: string) => {
        setOpen(true);
        setMessages((prev) => [
          ...prev,
          { id: nextMessageId(), role: "user", content: message },
        ]);
        thinkingRef.current = true;
        setThinking(true);
        try {
          const res = await rcChat({
            message,
            conversationId: conversationIdRef.current,
            pageContext: parsePageContext(
              location.pathname,
              user?.role,
              focusedHomeworkItemIdRef.current
            ),
          });
          await applyResponse(res);
        } catch (e) {
          const msg =
            e instanceof Error
              ? e.message
              : "RC could not complete that request";
          setSnackbarErrorMsg(msg);
          setMessages((prev) => [
            ...prev,
            {
              id: nextMessageId(),
              role: "assistant",
              content: `Sorry — ${msg}`,
            },
          ]);
        } finally {
          thinkingRef.current = false;
          setThinking(false);
          const next = sendQueueRef.current.shift();
          if (next) {
            void run(next);
          }
        }
      };

      void run(trimmed);
    },
    [location.pathname, user?.role, applyResponse, setSnackbarErrorMsg]
  );

  sendTextRef.current = sendText;

  const confirm = useCallback(
    async (token: string) => {
      setThinking(true);
      try {
        const res = await rcChat({
          conversationId,
          confirmToken: token,
          pageContext: parsePageContext(
            location.pathname,
            user?.role,
            focusedHomeworkItemIdRef.current
          ),
        });
        await applyResponse(res);
      } catch (e) {
        setSnackbarErrorMsg(
          e instanceof Error ? e.message : "Confirmation failed"
        );
      } finally {
        setThinking(false);
      }
    },
    [
      conversationId,
      location.pathname,
      user?.role,
      applyResponse,
      setSnackbarErrorMsg,
    ]
  );

  const cancelConfirm = useCallback(
    async (token: string) => {
      setThinking(true);
      try {
        const res = await rcChat({
          conversationId,
          cancelConfirmToken: token,
          pageContext: parsePageContext(
            location.pathname,
            user?.role,
            focusedHomeworkItemIdRef.current
          ),
        });
        await applyResponse(res);
      } catch (e) {
        setPendingConfirmations((prev) =>
          prev.filter((c) => c.confirmToken !== token)
        );
        setSnackbarErrorMsg(
          e instanceof Error ? e.message : "Could not cancel"
        );
      } finally {
        setThinking(false);
      }
    },
    [
      conversationId,
      location.pathname,
      user?.role,
      applyResponse,
      setSnackbarErrorMsg,
    ]
  );

  const toggleListen = useCallback(() => {
    if (listening) {
      stop();
    } else {
      setOpen(true);
      start();
    }
  }, [listening, start, stop]);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      // Avoid Cmd/Ctrl+Shift+R — browsers use that for hard refresh.
      if (
        (e.metaKey || e.ctrlKey) &&
        e.shiftKey &&
        (e.code === "Space" || e.key === " ")
      ) {
        e.preventDefault();
        setOpen(true);
        if (listening) {
          stop();
        } else {
          start();
        }
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [listening, start, stop]);

  const value = useMemo<RcContextValue>(
    () => ({
      open,
      setOpen,
      messages,
      listening,
      thinking,
      interim,
      speechError,
      speechSupported,
      pendingConfirmations,
      toggleListen,
      sendText,
      confirm,
      cancelConfirm,
      clearSpeechError: () => setSpeechError(null),
    }),
    [
      open,
      messages,
      listening,
      thinking,
      interim,
      speechError,
      speechSupported,
      pendingConfirmations,
      toggleListen,
      sendText,
      confirm,
      cancelConfirm,
      setSpeechError,
    ]
  );

  return (
    <RcContext.Provider value={value}>
      {children}
      <RcFab />
      <RcPanel />
    </RcContext.Provider>
  );
};

export default RcProvider;
